/** One logical line of a config file, already split into tokens. */
export interface TokenLine {
  tokens: string[]
  /** 1-based line number where the logical line started. */
  line: number
}

/**
 * Splits FortiOS config text into logical lines of tokens.
 *
 * Three details matter and each one is a real config in the wild:
 * - Quoted values may contain spaces (`set name "Allow LAN to WAN"`).
 * - Quoted values may span physical lines — embedded certificates and keys do this
 *   routinely — so a newline inside quotes does NOT end the logical line.
 * - `#` starts a comment only at the beginning of a logical line; FortiOS puts
 *   `#config-version=...` there, and a `#` inside a value is just a character.
 *
 * Never throws. An unterminated quote yields the tokens collected so far, because
 * a user pasting a truncated config should still get findings for the part that parsed.
 */
export function tokenize(text: string): TokenLine[] {
  const lines: TokenLine[] = []
  let tokens: string[] = []
  let lineNo = 1
  let startLine = 1
  let i = 0

  const flush = (): void => {
    if (tokens.length > 0) {
      lines.push({ tokens, line: startLine })
      tokens = []
    }
  }

  while (i < text.length) {
    const ch = text[i]!

    if (ch === '\n') {
      flush()
      lineNo++
      i++
      startLine = lineNo
      continue
    }

    if (ch === ' ' || ch === '\t' || ch === '\r') {
      i++
      continue
    }

    if (tokens.length === 0) startLine = lineNo

    // Comment, but only in leading position on a logical line.
    if (ch === '#' && tokens.length === 0) {
      while (i < text.length && text[i] !== '\n') i++
      continue
    }

    if (ch === '"') {
      i++
      let value = ''
      while (i < text.length) {
        const c = text[i]!
        if (c === '\\' && i + 1 < text.length) {
          const next = text[i + 1]!
          if (next === '\n') lineNo++
          value += next
          i += 2
          continue
        }
        if (c === '"') {
          i++
          break
        }
        if (c === '\n') lineNo++
        value += c
        i++
      }
      tokens.push(value)
      continue
    }

    let value = ''
    while (i < text.length) {
      const c = text[i]!
      if (c === ' ' || c === '\t' || c === '\r' || c === '\n' || c === '"') break
      value += c
      i++
    }
    // FortiOS writes an unset string as two literal single quotes — `set comment ''` —
    // rather than as an empty double-quoted string. Left as-is it surfaces in output as
    // a policy named "''", so normalise it to the empty value it actually represents.
    tokens.push(value === "''" ? '' : value)
  }

  flush()
  return lines
}
